import { AreaCatalogue, useI18n } from "@baton/i18n";
import { Button } from "@baton/ui";
import { useQuery } from "@tanstack/react-query";
import { Suspense, useEffect, useRef, useState } from "react";
import { meQuery } from "../app/queries.ts";
import { useRuntime } from "../app/RuntimeProvider.tsx";
import { signOut } from "../auth/session/signOut.ts";
import { useRouteMeta } from "../routes/meta.ts";
import { NoAccessSkeleton } from "../skeletons/Skeletons.tsx";
import { PORTAL_AREA } from "./catalogue.ts";
import { portalsInTeam } from "./standing.ts";

/**
 * 028 FR-028-058, `specs/015-web-frontend/contracts/screens.md` § 028 Client portal, No access page:
 * what a signed-in person who holds nothing in a Team sees at its Tickets routes. A stranger is told
 * only that they hold no access there, the Team named by the address the link carries and by nothing
 * the server knows, so the page tells them nothing; a client member whose portal the Team closed
 * (`GET /me` lists it with its status) is told the Team closed the client's tickets, naming both.
 * Either may sign out from it: the page stands outside every shell, and a client member whose only
 * tickets were closed holds no other screen that carries the account menu (slice 6, 028 T102).
 */
export type PortalNoAccessProps =
  | { readonly kind: "stranger"; readonly address: string }
  | { readonly kind: "closed"; readonly team: string; readonly client: string };

export function PortalNoAccess(props: PortalNoAccessProps) {
  return (
    <Suspense fallback={<NoAccessSkeleton />}>
      <AreaCatalogue area={PORTAL_AREA}>
        <Panel {...props} />
      </AreaCatalogue>
    </Suspense>
  );
}

function Panel(props: PortalNoAccessProps) {
  const { t } = useI18n();
  const runtime = useRuntime();
  const [leaving, setLeaving] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const title = props.kind === "closed" ? t("portal.noAccess.closedTitle", { client: props.client }) : t("portal.noAccess.title");
  useRouteMeta({ screen: { text: title }, focus: heading });
  return (
    <div className="b-no-access" role="alert">
      <h1 ref={heading} tabIndex={-1} className="b-no-access__title">
        {title}
      </h1>
      <p className="b-no-access__body">{props.kind === "closed" ? t("portal.noAccess.closedBody", { team: props.team, client: props.client }) : t("portal.noAccess.body", { address: props.address })}</p>
      <div className="b-no-access__actions">
        <Button
          variant="secondary"
          loading={leaving}
          onClick={() => {
            setLeaving(true);
            void signOut(runtime);
          }}
        >
          {t("nav.signOut")}
        </Button>
      </div>
    </div>
  );
}

/**
 * A client member's read of a client's tickets refused while they were in the portal shell (FR-028-010,
 * FR-028-058, US6 scenario 5): the Team closed the portal or removed them since `GET /me` was read, and
 * a client member opens no event stream to hear it. `/me` is read again before anything is drawn. Once
 * it holds no open portal of the Team, the Team's entry (`shell/layout/WorkspaceEntry.tsx`) leaves the
 * portal shell for the no-access page outside it, so this keeps the skeleton rather than drawing the
 * same page inside the shell first; while the person still holds another open portal of the Team, it
 * says what their standing on this client now is: the closed page naming the Team and the client while
 * the membership stands on a closed portal, else the stranger's page.
 */
export function ClientRefused({ ws, account }: { readonly ws: string; readonly account: string }) {
  const runtime = useRuntime();
  const me = useQuery(meQuery(runtime));
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    void runtime.queryClient.refetchQueries({ queryKey: meQuery(runtime).queryKey }).finally(() => {
      setChecked(true);
    });
  }, [runtime]);
  const portals = portalsInTeam(me.data, ws);
  if (!checked || !portals.some((portal) => portal.status === "open")) return <NoAccessSkeleton />;
  const held = portals.find((portal) => portal.account.id === account);
  return held?.status === "closed" ? <PortalNoAccess kind="closed" team={held.workspace.name} client={held.account.name} /> : <PortalNoAccess kind="stranger" address={ws} />;
}
